import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOnboarding } from '../../contexts/OnboardingContext';

const PADDING = 12; // Slightly more padding for better focus
const TOOLTIP_WIDTH = 360; // Slightly wider for readability
const TOOLTIP_EST_HEIGHT = 200;
const TOOLTIP_MARGIN = 20;

const TourOverlay: React.FC = () => {
    const { isActive, currentStepIndex, nextStep, skipTour, activeSteps } = useOnboarding();
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const step = activeSteps[currentStepIndex];

    // ─── Scroll element into view then capture its rect ───────────────────────
    const updateRect = useCallback(() => {
        if (!step) return;

        if (step.targetId === 'welcome-center') {
            setTargetRect(null);
            return;
        }

        const element = document.getElementById(step.targetId);
        if (!element) {
            setTargetRect(null);
            return;
        }

        // Improved scroll logic: ensure element is visible with padding
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Wait for scroll to settle
        setTimeout(() => {
            const rect = element.getBoundingClientRect();
            setTargetRect(rect);

            // Check if tooltip will fit. If not, nudge the view.
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            const needed = TOOLTIP_EST_HEIGHT + TOOLTIP_MARGIN * 2;

            if (spaceBelow < needed && spaceAbove < needed) {
                // If it doesn't fit above OR below, we might need to scroll the element
                // to the top or bottom to make room.
                if (spaceBelow < spaceAbove) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'end' });
                } else {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }

                setTimeout(() => {
                    setTargetRect(element.getBoundingClientRect());
                }, 400);
            }
        }, 500);
    }, [step]);

    useEffect(() => {
        if (!isActive) return;
        updateRect();

        const onResize = () => updateRect();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [isActive, step, currentStepIndex, updateRect]);

    if (!isActive || !step) return null;

    // ─── Spotlight geometry ───────────────────────────────────────────────────
    const spotTop = targetRect && !isNaN(targetRect.top) ? targetRect.top - PADDING : 0;
    const spotLeft = targetRect && !isNaN(targetRect.left) ? targetRect.left - PADDING : 0;
    const spotWidth = targetRect && !isNaN(targetRect.width) ? Math.max(0, targetRect.width + PADDING * 2) : 0;
    const spotHeight = targetRect && !isNaN(targetRect.height) ? Math.max(0, targetRect.height + PADDING * 2) : 0;

    // ─── Tooltip position (always fully within viewport) ─────────────────────
    const isCenter = !targetRect;
    let tooltipTop: number;
    let tooltipLeft: number;

    if (isCenter) {
        tooltipTop = window.innerHeight / 2 - TOOLTIP_EST_HEIGHT / 2;
        tooltipLeft = window.innerWidth / 2 - TOOLTIP_WIDTH / 2;
    } else {
        const belowY = targetRect!.bottom + 24;
        const aboveY = targetRect!.top - TOOLTIP_EST_HEIGHT - 24;

        // Prefer below; fallback above; last resort — center
        if (belowY + TOOLTIP_EST_HEIGHT + TOOLTIP_MARGIN <= window.innerHeight) {
            tooltipTop = belowY;
        } else if (aboveY >= TOOLTIP_MARGIN) {
            tooltipTop = aboveY;
        } else {
            tooltipTop = window.innerHeight / 2 - TOOLTIP_EST_HEIGHT / 2;
        }

        // Clamp horizontally
        tooltipLeft = Math.max(
            TOOLTIP_MARGIN,
            Math.min(
                window.innerWidth - TOOLTIP_WIDTH - TOOLTIP_MARGIN,
                targetRect!.left + (targetRect!.width / 2) - (TOOLTIP_WIDTH / 2) // Center relative to target
            )
        );

        // Final vertical clamp
        tooltipTop = Math.max(
            TOOLTIP_MARGIN,
            Math.min(window.innerHeight - TOOLTIP_EST_HEIGHT - TOOLTIP_MARGIN, tooltipTop)
        );
    }

    return (
        <AnimatePresence>
            <motion.div
                key="tour-overlay-root"
                className="fixed inset-0 z-[100000] pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
            >
                {/* SVG mask — dark backdrop with spotlight cutout */}
                <svg
                    className="absolute inset-0 w-full h-full pointer-events-auto"
                    style={{ cursor: 'default' }}
                    onClick={skipTour}
                >
                    <defs>
                        <mask id="spotlight-mask">
                            <rect width="100%" height="100%" fill="white" />
                            {targetRect && (
                                <motion.rect
                                    key={step.targetId}
                                    animate={{
                                        x: spotLeft,
                                        y: spotTop,
                                        width: spotWidth,
                                        height: spotHeight,
                                    }}
                                    transition={{ type: 'spring', stiffness: 280, damping: 30 }}
                                    rx="16" ry="16" // Slightly rounder spotlight
                                    fill="black"
                                />
                            )}
                        </mask>
                    </defs>
                    <rect
                        width="100%" height="100%"
                        fill="rgba(0,0,0,0.8)" // Darker backdrop for more focus
                        mask="url(#spotlight-mask)"
                    />
                </svg>

                {/* Animated blue border ring around target */}
                {targetRect && (
                    <motion.div
                        key={`border-${step.targetId}`}
                        className="absolute pointer-events-none rounded-2xl"
                        style={{
                            boxShadow: '0 0 0 3px rgba(37,99,235,1), 0 0 40px rgba(37,99,235,0.6)',
                        }}
                        animate={{
                            top: spotTop,
                            left: spotLeft,
                            width: spotWidth,
                            height: spotHeight,
                        }}
                        transition={{ type: 'spring', stiffness: 280, damping: 30 }}
                    />
                )}

                {/* Tooltip card — always on top, clamped inside viewport */}
                <motion.div
                    ref={tooltipRef}
                    key={`tooltip-${currentStepIndex}`}
                    className="pointer-events-auto"
                    style={{
                        position: 'fixed',
                        zIndex: 100001,
                        width: TOOLTIP_WIDTH,
                    }}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{
                        opacity: 1,
                        scale: 1,
                        y: 0,
                        top: tooltipTop,
                        left: tooltipLeft,
                    }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                >
                    <div
                        className="rounded-3xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] border border-white/20"
                        style={{
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', // Brand bright blue
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        {/* Glow effect */}
                        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />

                        <div className="p-6 relative z-10">
                            {/* Step progress dots */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex gap-1.5">
                                    {activeSteps.map((_, i) => (
                                        <div
                                            key={i}
                                            className="h-1.5 rounded-full transition-all duration-300"
                                            style={{
                                                width: i === currentStepIndex ? 24 : 6,
                                                background: i <= currentStepIndex
                                                    ? 'rgba(255,255,255,0.95)'
                                                    : 'rgba(255,255,255,0.25)',
                                            }}
                                        />
                                    ))}
                                </div>
                                <span className="text-[12px] text-white/60 font-bold tracking-wider uppercase">
                                    Step {currentStepIndex + 1} of {activeSteps.length}
                                </span>
                            </div>

                            <h3 className="text-xl font-extrabold text-white mb-2 leading-tight">
                                {step.title}
                            </h3>
                            <p className="text-[15px] text-white/90 leading-relaxed mb-6 font-medium">
                                {step.content}
                            </p>

                            <div className="flex items-center justify-between">
                                <button
                                    onClick={skipTour}
                                    className="text-sm text-white/60 hover:text-white transition-colors font-bold uppercase tracking-tight"
                                >
                                    End Tour
                                </button>
                                <button
                                    onClick={nextStep}
                                    className="px-6 py-2.5 rounded-2xl text-sm font-black text-blue-700 bg-white hover:bg-blue-50 transition-all hover:scale-105 active:scale-95 shadow-xl"
                                >
                                    {currentStepIndex === activeSteps.length - 1 ? 'Finish →' : 'Continue →'}
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};


export default TourOverlay;
